import React, { useState } from 'react';
import { useAuth } from './AuthProvider';
import './AuthForms.css';

interface ForgotPasswordFormProps {
  onSuccess?: () => void;
  onBackToSignIn?: () => void;
}

export const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ 
  onSuccess, 
  onBackToSignIn 
}) => {
  const { forgotPassword, confirmPassword } = useAuth();
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [formData, setFormData] = useState({
    email: '',
    code: '',
    newPassword: '',
    confirmNewPassword: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const validateEmailForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.email) {
      newErrors.email = 'Please enter your email address';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePasswordForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Code validation
    if (!formData.code) {
      newErrors.code = 'Please enter the reset code';
    } else if (formData.code.length !== 6) {
      newErrors.code = 'Reset code must be 6 digits';
    } else if (!/^\d+$/.test(formData.code)) {
      newErrors.code = 'Reset code must contain only numbers';
    }

    // Password validation
    if (!formData.newPassword) {
      newErrors.newPassword = 'Please enter a new password';
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters long';
    } else if (!/(?=.*[a-z])/.test(formData.newPassword)) {
      newErrors.newPassword = 'Password must include at least one lowercase letter';
    } else if (!/(?=.*[A-Z])/.test(formData.newPassword)) {
      newErrors.newPassword = 'Password must include at least one uppercase letter';
    } else if (!/(?=.*\d)/.test(formData.newPassword)) {
      newErrors.newPassword = 'Password must include at least one number';
    }

    // Confirm password validation
    if (!formData.confirmNewPassword) {
      newErrors.confirmNewPassword = 'Please confirm your new password';
    } else if (formData.newPassword !== formData.confirmNewPassword) {
      newErrors.confirmNewPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateEmailForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      await forgotPassword(formData.email);
      setStep('code');
    } catch (error: any) {
      console.error('Forgot password error:', error);
      
      let errorMessage = 'Something went wrong. Please try again.';
      
      if (error.code === 'UserNotFoundException') {
        errorMessage = 'No account found with this email address. Please check your email or create a new account.';
      } else if (error.code === 'LimitExceededException') {
        errorMessage = 'Too many requests. Please wait a few minutes and try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setErrors({ general: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validatePasswordForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      await confirmPassword(formData.email, formData.code, formData.newPassword);
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error('Confirm password error:', error);
      
      let errorMessage = 'Something went wrong. Please try again.';
      
      if (error.code === 'CodeMismatchException') {
        errorMessage = 'The reset code is incorrect. Please check and try again.';
      } else if (error.code === 'ExpiredCodeException') {
        errorMessage = 'The reset code has expired. Please request a new one.';
      } else if (error.code === 'InvalidPasswordException') {
        errorMessage = 'Password does not meet requirements. Please check the password rules.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setErrors({ general: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleCodeChange = (value: string) => {
    // Only allow numbers and limit to 6 digits
    const numericValue = value.replace(/\D/g, '').slice(0, 6);
    handleInputChange('code', numericValue);
  };

  if (step === 'email') {
    return (
      <div className="auth-form-container">
        <div className="auth-form">
          <h1 className="auth-title">Reset Your Password</h1>
          <p className="auth-subtitle">
            Enter your email address and we'll send you a code to reset your password
          </p>

          {errors.general && (
            <div className="error-message general-error" role="alert">
              {errors.general}
            </div>
          )}

          <form onSubmit={handleEmailSubmit} noValidate>
            {/* Email Field */}
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                className={`form-input ${errors.email ? 'error' : ''}`}
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="Enter your email address"
                required
                autoComplete="email"
                aria-describedby={errors.email ? 'email-error' : undefined}
              />
              {errors.email && (
                <div id="email-error" className="error-message" role="alert">
                  {errors.email}
                </div>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="auth-button primary"
              disabled={isLoading}
            >
              {isLoading ? 'Sending Reset Code...' : 'Send Reset Code'}
            </button>
          </form>

          {/* Back to Sign In */}
          <div className="auth-switch">
            <p>Remember your password?</p>
            <button
              type="button"
              className="auth-button secondary"
              onClick={onBackToSignIn}
            >
              Back to Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-form-container">
      <div className="auth-form">
        <h1 className="auth-title">Enter Reset Code</h1>
        <p className="auth-subtitle">
          We've sent a reset code to <strong>{formData.email}</strong>. 
          Enter the code and your new password below.
        </p>

        {errors.general && (
          <div className="error-message general-error" role="alert">
            {errors.general}
          </div>
        )}

        <form onSubmit={handlePasswordSubmit} noValidate>
          {/* Reset Code Field */}
          <div className="form-group">
            <label htmlFor="code" className="form-label">
              Reset Code
            </label>
            <input
              id="code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              className={`form-input code-input ${errors.code ? 'error' : ''}`}
              value={formData.code}
              onChange={(e) => handleCodeChange(e.target.value)}
              placeholder="Enter 6-digit code"
              required
              autoComplete="one-time-code"
              aria-describedby="code-help code-error"
              maxLength={6}
            />
            <div id="code-help" className="form-help">
              Check your email for a 6-digit reset code.
            </div>
            {errors.code && (
              <div id="code-error" className="error-message" role="alert">
                {errors.code}
              </div>
            )}
          </div>

          {/* New Password Field */}
          <div className="form-group">
            <label htmlFor="newPassword" className="form-label">
              New Password
            </label>
            <div className="password-input-container">
              <input
                id="newPassword"
                type={showPassword ? 'text' : 'password'}
                className={`form-input ${errors.newPassword ? 'error' : ''}`}
                value={formData.newPassword}
                onChange={(e) => handleInputChange('newPassword', e.target.value)}
                placeholder="Enter your new password"
                required
                autoComplete="new-password"
                aria-describedby="password-requirements new-password-error"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            <div id="password-requirements" className="password-requirements">
              <p>Your new password must include:</p>
              <ul>
                <li>At least 8 characters</li>
                <li>One uppercase letter (A-Z)</li>
                <li>One lowercase letter (a-z)</li>
                <li>One number (0-9)</li>
              </ul>
            </div>
            {errors.newPassword && (
              <div id="new-password-error" className="error-message" role="alert">
                {errors.newPassword}
              </div>
            )}
          </div>

          {/* Confirm New Password Field */}
          <div className="form-group">
            <label htmlFor="confirmNewPassword" className="form-label">
              Confirm New Password
            </label>
            <input
              id="confirmNewPassword"
              type={showPassword ? 'text' : 'password'}
              className={`form-input ${errors.confirmNewPassword ? 'error' : ''}`}
              value={formData.confirmNewPassword}
              onChange={(e) => handleInputChange('confirmNewPassword', e.target.value)}
              placeholder="Enter your new password again"
              required
              autoComplete="new-password"
              aria-describedby={errors.confirmNewPassword ? 'confirm-new-password-error' : undefined}
            />
            {errors.confirmNewPassword && (
              <div id="confirm-new-password-error" className="error-message" role="alert">
                {errors.confirmNewPassword}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="auth-button primary"
            disabled={isLoading}
          >
            {isLoading ? 'Resetting Password...' : 'Reset Password'}
          </button>
        </form>

        {/* Back to Email Step */}
        <div className="auth-switch">
          <p>Need to use a different email?</p>
          <button
            type="button"
            className="auth-button secondary"
            onClick={() => setStep('email')}
          >
            Back to Email
          </button>
        </div>
      </div>
    </div>
  );
};